import { DomApplier } from '../cssFeatures.types'
import { ClassicCss, Css, CssObject, SelectorObj } from '../../shared.types'
import { fromClassicCss } from '../../utils/fromClassicCss'
import { EMPTY_OBJECT } from '../../utils/constants'
import { getVariantSelectors, variantsComparator } from '../../utils/variantsUtils'
import { CompVariantsViewItem } from '@wix/thunderbolt-catharsis'
import { CssContext } from '../componentCss.types'
import { getDisplayedId } from '@wix/thunderbolt-commons'
import { getInflatedRepeaterIdSelector } from '../../utils/selectorsUtils'
import { isEqual } from 'lodash'

const getSelectorObj = (
	compId: string,
	targetId: string,
	idSelector: string,
	breakpointId: string,
	css: Css<CssObject | undefined>,
	themeVariants: CompVariantsViewItem | null,
	accumulatedData: CssContext
): SelectorObj => {
	if (!css) {
		return EMPTY_OBJECT
	}

	if (css.type === 'classic') {
		return fromClassicCss(css.css as ClassicCss['css'], idSelector)
	} else {
		const cssInBreakPoint = css.css[breakpointId]
		if (!cssInBreakPoint) {
			return EMPTY_OBJECT
		}

		return Object.entries(cssInBreakPoint)
			.sort(variantsComparator(themeVariants!))
			.reduce<SelectorObj>((acc, [variantKey, cssObj]) => {
				if (!cssObj) {
					return acc
				}

				const domSelector = getVariantSelectors(
					variantKey,
					themeVariants,
					compId,
					accumulatedData,
					targetId
				).join(',')
				acc[domSelector] = cssObj

				return acc
			}, {})
	}
}

export const compCssMappersDomApplier: DomApplier<'compCssMappers'> = (
	{ id: compId },
	idSelector,
	breakpointId,
	{ compCssMappers, themeVariants },
	accumulatedData
) => {
	if (!compCssMappers) {
		return EMPTY_OBJECT
	}

	const { '': templateCss, ...repeatedItemsCss } = compCssMappers
	const templateCssResult = getSelectorObj(
		compId,
		compId,
		idSelector,
		breakpointId,
		templateCss,
		themeVariants,
		accumulatedData
	)!

	if (!Object.keys(repeatedItemsCss).length) {
		return templateCssResult
	}

	return Object.entries(repeatedItemsCss).reduce<SelectorObj>((acc, [context, compCss]) => {
		if (isEqual(compCss, templateCss)) {
			return acc
		}

		const displayedId = getDisplayedId(compId, context)
		const contextCssResult = getSelectorObj(
			compId,
			displayedId,
			getInflatedRepeaterIdSelector(displayedId),
			breakpointId,
			compCss,
			themeVariants,
			accumulatedData
		)

		return contextCssResult ? Object.assign(acc, contextCssResult) : acc
	}, templateCssResult)
}
